// src/utils/netSheet.js
// The Estimate of Proceeds (seller net sheet), Henry 10/06/2026, October 6th meeting #13. Pure: no React, no
// Firestore. The agent fills it in on the deal page's Work tab (deal.netSheet), shares it to the seller's
// portal, and EVERY seller on the listing signs there. The server twin of the math is
// functions-api/netSheet.js (same rules, change both together).
//
// deal.netSheet = {
//   salePrice,                                   // dollars
//   costLines: [{ id, label, kind, amount, percent, note }],   // closing costs; kind "amount" | "percent"
//   payoffs:   [{ id, label, kind: "amount", amount, note }],  // mortgage payoff(s)
//   workLines: [{ id, label, kind: "amount", amount, note, sourceId }], // work the seller owes for
//   status: "draft" | "shared", shareId, sharedAt, sharedBy, updatedAt, updatedBy,
// }
// A percent line (commission, state deed tax) is stored as the percent; its dollars follow the sale price.
// deal.netSheetSigned (server only) = { shareId, completedAt, signers, pdfDriveFileId, pdfUrl }.
//
// AUTO-SYNC (Henry 10/06/2026: "when an offer is accepted, everything automatically updates, so we don't
// have to be manually inputting things"). A line may carry `link` (a NET_SHEET_LINKS key): it then shows the
// deal's own figure (the commission %, the seller paid closing costs, the admin fee) instead of its stored
// one. The agent typing over a deal figure sets `manual: true` ("Typed In"); "Use Deal Figure" clears it.
// The estimate itself carries salePriceLink ("salePrice": the accepted offer, else the list price) with
// salePriceManual, and workFromDeal (the owed work lines follow the deal; skipWork lists rows the agent
// removed). The stored figures stay as the fallback when the deal has none. resolveEstimate() is the ONE
// place this happens; the server twin (functions-api/netSheet.js resolveEstimate) must agree, and the card,
// the portal and the PDF all use the RESOLVED figures. The same estimate lives on a listing lead
// (lead.netSheet) and rides onto the deal when the lead becomes a listing (carryNetSheetToDeal).
// Templates: Settings > Team Defaults keeps several named line lists (teamDefaults.netSheetTemplates).

import { owedAtClosing } from "./costReceiptTracker";
import { effectiveAdminFee, isAdminFeeMissing, isAdminFeeWaived } from "./adminFee";

const isObj = (v) => Boolean(v) && typeof v === "object" && !Array.isArray(v);
const str = (v, n) =>
  String(v == null ? "" : v)
    .replace(/[\r\n\t]+/g, " ")
    .trim()
    .slice(0, n);
const cents = (n) => Math.round(n * 100) / 100;
const MAX_LINES = 60;

export function money(v) {
  const n = typeof v === "number" ? v : Number(String(v == null ? "" : v).replace(/[^\d.]/g, ""));
  return Number.isFinite(n) && n > 0 ? cents(n) : 0;
}

export function percent(v) {
  const n = typeof v === "number" ? v : Number(String(v == null ? "" : v).replace(/[^\d.]/g, ""));
  return Number.isFinite(n) && n > 0 ? Math.min(100, Math.round(n * 1000) / 1000) : 0;
}

// The closing-cost lines a new estimate starts with, taken from Henry's own Estimate of Proceeds template
// (~/Downloads Netsheet.xlsx, sent 10/06/2026; first built from "Spring Creek Net sheets.pdf"), in his
// order. The sale price, the present mortgage and the repair lines are not here: they have their own
// sections. A Team Lead edits this list in Settings > Team Defaults (teamDefaults.netSheetDefaultLines).
export const DEFAULT_NET_SHEET_LINES = [
  { id: "interestAdj", label: "Interest Adjustment (1 Month, 2 Months if FHA)", kind: "amount", amount: 0, note: "Approximately 1 month" },
  { id: "interestAdj2", label: "Interest Adjustment (Contract for Deed, 2nd Mortgage), 1 Month", kind: "amount", amount: 0 },
  { id: "courier", label: "Courier and Processing Fee ($25 per Package)", kind: "amount", amount: 0 },
  { id: "cdDiscount", label: "Contract for Deed Discount", kind: "amount", amount: 0 },
  { id: "sellerPaidCosts", label: "Seller Paid Buyer Closing Costs", kind: "amount", amount: 0, note: "Negotiable", link: "sellerPaidCosts" },
  { id: "vaFunding", label: "VA Funding Fee (per Purchase Agreement)", kind: "amount", amount: 0 },
  { id: "vaFees", label: "VA Closing, Service and Buyer Financing Fees (About $475)", kind: "amount", amount: 0 },
  { id: "fhaFees", label: "FHA Tax Service and Buyer Financing Fees ($165)", kind: "amount", amount: 0 },
  { id: "assocDues", label: "Association Dues", kind: "amount", amount: 0, note: "Pro-rated" },
  { id: "assocLetter", label: "Association Letter Prep Fee ($100 to $300)", kind: "amount", amount: 0 },
  { id: "assessSearch", label: "Special Assessment Search and Review", kind: "amount", amount: 50 },
  { id: "assessLevied", label: "Special Assessments, Levied and Approved of Record", kind: "amount", amount: 0 },
  { id: "assessPending", label: "Special Assessments, Pending (200% Escrowed)", kind: "amount", amount: 0 },
  { id: "commissionListing", label: "Brokerage Commission, Listing Side", kind: "percent", percent: 3.5, link: "commissionListing" },
  { id: "commissionBuyer", label: "Brokerage Commission, Buyer Side", kind: "percent", percent: 2.7, link: "commissionBuyer" },
  { id: "adminFee", label: "Broker Administrative Commission", kind: "amount", amount: 599, link: "adminFee" },
  { id: "closingService", label: "Closing Service Fee", kind: "amount", amount: 595 },
  { id: "deedTax", label: "State Deed Tax ($3.40 per $1,000)", kind: "percent", percent: 0.34 },
  { id: "abstract", label: "Abstract Examination or RPA Fees", kind: "amount", amount: 0 },
  { id: "recording", label: "Recording, Service and Processing Fees (About $65 to $200)", kind: "amount", amount: 200 },
  { id: "taxes", label: "Balance of Real Estate Taxes Due at Closing", kind: "amount", amount: 0, note: "Pro-rated" },
  { id: "otherExpenses", label: "Other Expenses (Survey, Water Test, Soil Test)", kind: "amount", amount: 0 },
  { id: "warranty", label: "Home Warranty", kind: "amount", amount: 0, note: "Optional" },
  { id: "radon", label: "Radon Test ($200) or Radon Mitigation System ($1,585)", kind: "amount", amount: 0 },
  { id: "inspectionRepairs", label: "Inspection Repair Items", kind: "amount", amount: 0 },
  { id: "tish", label: "Local Building Inspection (TISH)", kind: "amount", amount: 0, note: "Optional" },
  { id: "conservation", label: "Conservation Fee", kind: "amount", amount: 5 },
  { id: "other", label: "Other", kind: "amount", amount: 0 },
  { id: "repairs", label: "Repairs and Enhancements", kind: "amount", amount: 0 },
  { id: "staging", label: "Staging", kind: "amount", amount: 0 },
];

// Henry's sheet lines 2 and 4, plus its "Less New Contract for Deed to Seller": a line left at $0 never shows.
export const DEFAULT_PAYOFFS = [
  { id: "mortgage1", label: "Present Mortgage", kind: "amount", amount: 0, note: "" },
  { id: "mortgage2", label: "Payoff (2nd Mortgage, Home Improvement Loan)", kind: "amount", amount: 0, note: "" },
  { id: "newCd", label: "New Contract for Deed to Seller", kind: "amount", amount: 0, note: "" },
];

export const NET_SHEET_DISCLAIMER = "This is an estimate. Final figures come from the closing statement.";

// The deal figures a line can follow (the `link` key), in plain words for the template editor's "Follows"
// picker. The sale price is not here: it is the estimate's own salePriceLink.
export const NET_SHEET_LINKS = {
  commissionListing: "Listing Commission (Deal)",
  commissionBuyer: "Buyer Agent Commission (Deal)",
  sellerPaidCosts: "Seller Paid Closing Costs (Deal)",
  adminFee: "Broker Admin Fee (Deal)",
};
const LINK_KEYS = Object.keys(NET_SHEET_LINKS);

export function cleanLine(raw, i = 0, prefix = "l") {
  const l = isObj(raw) ? raw : {};
  const kind = l.kind === "percent" ? "percent" : "amount";
  const out = {
    id: str(l.id, 40) || `${prefix}${i}`,
    label: str(l.label, 120),
    kind,
    amount: kind === "amount" ? money(l.amount) : 0,
    percent: kind === "percent" ? percent(l.percent) : 0,
    note: str(l.note, 60),
  };
  if (l.sourceId) out.sourceId = str(l.sourceId, 80);
  if (LINK_KEYS.includes(l.link)) out.link = l.link;
  if (l.manual === true) out.manual = true;
  return out;
}

const cleanLines = (arr, prefix) =>
  (Array.isArray(arr) ? arr : []).slice(0, MAX_LINES).map((l, i) => cleanLine(l, i, prefix));

// The team's default list: Settings > Team Defaults when saved, else Henry's sheet above. A list saved before
// links existed (10/06/2026 morning) gets the links of Henry's sheet back by line id, so it syncs too.
export function defaultLinesFrom(teamDefaults) {
  const saved = teamDefaults && Array.isArray(teamDefaults.netSheetDefaultLines) ? teamDefaults.netSheetDefaultLines : null;
  if (!(saved && saved.length)) return cleanLines(DEFAULT_NET_SHEET_LINES, "d");
  const linkById = Object.fromEntries(DEFAULT_NET_SHEET_LINES.filter((l) => l.link).map((l) => [l.id, l.link]));
  return cleanLines(saved, "d").map((l) => (!l.link && linkById[l.id] ? { ...l, link: linkById[l.id] } : l));
}

// ---- Templates (Henry 10/06/2026: "Standard", then "FHA", "VA", "Cash" ...) ----
// teamDefaults.netSheetTemplates = [{ id, name, isDefault, lines, payoffs }]. Until one is saved the team has
// one template, "Standard", made from the old single list (netSheetDefaultLines) or Henry's sheet. Exactly one
// template is the default (the first one when none is marked).
export function cleanTemplate(raw, i = 0) {
  const t = isObj(raw) ? raw : {};
  return {
    id: str(t.id, 40) || `t${i}`,
    name: str(t.name, 60) || `Template ${i + 1}`,
    isDefault: t.isDefault === true,
    lines: cleanLines(t.lines, "d"),
    payoffs: cleanLines(Array.isArray(t.payoffs) ? t.payoffs : DEFAULT_PAYOFFS, "p"),
  };
}

export function templatesFrom(teamDefaults) {
  const saved = teamDefaults && Array.isArray(teamDefaults.netSheetTemplates) ? teamDefaults.netSheetTemplates.filter(isObj) : [];
  const list = saved.slice(0, 20).map(cleanTemplate);
  if (!list.length) {
    return [{ id: "standard", name: "Standard", isDefault: true, lines: defaultLinesFrom(teamDefaults), payoffs: cleanLines(DEFAULT_PAYOFFS, "p") }];
  }
  const first = list.findIndex((t) => t.isDefault);
  return list.map((t, i) => ({ ...t, isDefault: i === (first >= 0 ? first : 0) }));
}

export const defaultTemplate = (templates) => (templates || []).find((t) => t.isDefault) || (templates || [])[0] || null;

// The price an estimate starts from: the accepted offer when there is one, else the list price (a listing
// lead: its list price, else its estimated value).
export function startingPrice(deal) {
  const d = deal || {};
  return money(d.offerPrice) || money(d.listPrice) || money(d.suggestedPrice) || money(d.estValue) || 0;
}

// A listing lead read as a figure source: its List Price, else its Estimated Value. Nothing else on a lead
// is a deal figure (no offer, no commission, no admin fee of its own).
export function leadFigureSource(lead) {
  const l = lead || {};
  return { listPrice: l.listPrice, suggestedPrice: l.estValue, isLead: true };
}

// The deal's "Pay at Closing" costs and the agent-fronted costs not yet paid back (the Pre-Market
// Improvements and Inspection Requests lists, the same rule as costReceiptTracker.owedAtClosing), as
// "Work the Seller Owes For" lines. sourceId remembers the row, so pulling again never doubles a line.
export function workLinesFromDeal(deal) {
  const out = [];
  [["preMarketImprovements", "pm"], ["inspectionRequests", "ir"]].forEach(([key, tag]) => {
    (Array.isArray(deal && deal[key]) ? deal[key] : []).forEach((it, i) => {
      if (!it || !owedAtClosing([it])) return;
      const label = [str(it.description, 80) || "Work", it.vendor ? `(${str(it.vendor, 40)})` : ""].filter(Boolean).join(" ");
      out.push(cleanLine({ id: `w_${tag}_${str(it.id, 30) || i}`, label, kind: "amount", amount: it.cost, sourceId: `${key}:${str(it.id, 40) || i}` }));
    });
  });
  return out;
}

// Add the deal's owed work lines that are not on the estimate yet (by sourceId). Existing lines are kept as
// the agent edited them.
export function mergeWorkLines(current, fromDeal) {
  const have = new Set((current || []).map((l) => l.sourceId).filter(Boolean));
  return [...(current || []), ...(fromDeal || []).filter((l) => !have.has(l.sourceId))];
}

// A new estimate for a listing (or a listing lead, or the calculator): the chosen template's lines (the
// team's default template when none is picked; the Team Defaults admin fee on its line), its payoffs and the
// deal's owed work. The sale price and the work follow the deal from here on.
export function blankEstimate(deal, teamDefaults, template) {
  const t = template || defaultTemplate(templatesFrom(teamDefaults));
  const fee = teamDefaults && Number(teamDefaults.brokerAdminFee);
  const costLines = (t ? t.lines : defaultLinesFrom(teamDefaults)).map((l) =>
    l.id === "adminFee" && Number.isFinite(fee) && fee >= 0 && teamDefaults.brokerAdminFee !== "" ? { ...l, amount: money(fee) } : l,
  );
  return {
    salePrice: startingPrice(deal),
    salePriceLink: "salePrice",
    salePriceManual: false,
    workFromDeal: true,
    skipWork: [],
    templateId: t ? t.id : "",
    templateName: t ? t.name : "",
    costLines,
    payoffs: t ? t.payoffs : cleanLines(DEFAULT_PAYOFFS, "p"),
    workLines: workLinesFromDeal(deal),
    status: "draft",
    shareId: "",
  };
}

// ---- Auto-sync: the deal's figures ----

// Every figure the deal (or lead) holds that a line can follow, as { kind, amount|percent, from } where from
// is "deal" (the deal's own figure) or "team" (Team Defaults, e.g. a deal with no admin fee entered). null =
// the deal has no figure, so the line keeps its stored one.
//   salePrice          offerPrice (the accepted offer), else listPrice, else suggestedPrice (lead: estValue)
//   commissionListing  flatfee ? flatfeeamount : sellersidecommissionrate, else totalcommissionrate (%)
//   commissionBuyer    buyersidecommissionrate (%)
//   sellerPaidCosts    sellerPaidClosingCosts ($, Under Contract Milestones)
//   adminFee           brokeradminfee, $0 when Waived, the Team Defaults fee when missing (adminFee.js)
//   work               the owed Pre-Market Improvements and Inspection Requests rows
export function dealFigures(source, teamDefaults) {
  const d = isObj(source) ? source : {};
  const lines = {};
  if (d.flatfee === true && money(d.flatfeeamount) > 0) lines.commissionListing = { kind: "amount", amount: money(d.flatfeeamount), from: "deal" };
  else if (percent(d.sellersidecommissionrate) || percent(d.totalcommissionrate)) {
    lines.commissionListing = { kind: "percent", percent: percent(d.sellersidecommissionrate) || percent(d.totalcommissionrate), from: "deal" };
  }
  if (percent(d.buyersidecommissionrate)) lines.commissionBuyer = { kind: "percent", percent: percent(d.buyersidecommissionrate), from: "deal" };
  if (money(d.sellerPaidClosingCosts)) lines.sellerPaidCosts = { kind: "amount", amount: money(d.sellerPaidClosingCosts), from: "deal" };
  const feeFromDeal = !d.isLead && (isAdminFeeWaived(d) || !isAdminFeeMissing(d.brokeradminfee));
  lines.adminFee = { kind: "amount", amount: money(effectiveAdminFee(d.isLead ? {} : d, teamDefaults)), from: feeFromDeal ? "deal" : "team" };
  return { salePrice: startingPrice(d), lines, work: d.isLead ? [] : workLinesFromDeal(d) };
}

// The estimate with the deal's figures put in: linked lines that are not typed in show the deal's figure, the
// sale price follows the accepted offer, and the owed work follows the deal (new rows join, rows no longer
// owed leave, rows the agent removed stay out). Each line comes back with `from` ("deal" | "team" | "") and
// the estimate with salePriceFrom, for the "From the Deal" markers. Pure; the server twin agrees.
export function resolveEstimate(raw, source, teamDefaults) {
  const e = normalizeEstimate(raw);
  const f = dealFigures(source, teamDefaults);
  const followsPrice = e.salePriceLink === "salePrice" && !e.salePriceManual && f.salePrice > 0;
  const apply = (l) => {
    const fig = l.link && !l.manual ? f.lines[l.link] : null;
    if (!fig) return { ...l, from: "" };
    return { ...l, kind: fig.kind, amount: fig.kind === "amount" ? fig.amount : 0, percent: fig.kind === "percent" ? fig.percent : 0, from: fig.from };
  };
  let workLines = e.workLines.map((l) => ({ ...l, from: "" }));
  if (e.workFromDeal) {
    const owed = new Map(f.work.map((w) => [w.sourceId, w]));
    workLines = [];
    e.workLines.forEach((l) => {
      if (!l.sourceId || l.manual) workLines.push({ ...l, from: "" });
      else if (owed.has(l.sourceId)) workLines.push({ ...l, label: l.label || owed.get(l.sourceId).label, amount: owed.get(l.sourceId).amount, from: "deal" });
    });
    const have = new Set(workLines.map((l) => l.sourceId).filter(Boolean));
    const skip = new Set(e.skipWork);
    f.work.forEach((w) => {
      if (!have.has(w.sourceId) && !skip.has(w.sourceId)) workLines.push({ ...w, from: "deal" });
    });
  }
  return {
    ...e,
    salePrice: followsPrice ? f.salePrice : e.salePrice,
    salePriceFrom: followsPrice ? "deal" : "",
    costLines: e.costLines.map(apply),
    payoffs: e.payoffs.map(apply),
    workLines,
  };
}

// Back from the editor to what is stored: a line still showing the deal's figure keeps its stored fallback
// (so the template's 2.7% comes back if a contract falls through), every other line is kept as edited.
export function unresolveLines(next, stored) {
  const byId = new Map((stored || []).map((l) => [l.id, l]));
  return (next || []).map((l) => {
    const { from, ...rest } = l;
    const before = byId.get(l.id);
    if (from && l.link && !l.manual && before) return { ...rest, kind: before.kind, amount: before.amount, percent: before.percent };
    return rest;
  });
}

// A fingerprint of exactly what a seller sees: the sale price and every line's label, dollars and note. The
// server hashes the same text (functions-api/netSheet.js figuresHash), so keep the two identical.
export function figuresKey(computed) {
  const c = computed || computeEstimate({});
  const pick = (lines) => (lines || []).map((l) => [l.label, l.total, l.note]);
  return JSON.stringify([c.salePrice, pick(c.costs), pick(c.payoffs), pick(c.work)]);
}

// A listing lead's estimate onto the new listing deal (Henry 10/06/2026): the same lines and links, as a
// draft (never shared), so the deal's figures take over on their own. null when the lead has none.
export function carryNetSheetToDeal(leadNetSheet) {
  if (!isObj(leadNetSheet)) return null;
  const e = normalizeEstimate(leadNetSheet);
  if (!e.costLines.length && !e.payoffs.length && !e.workLines.length && !e.salePrice) return null;
  return { ...e, status: "draft", shareId: "", sharedAt: 0, carriedFromLead: true, updatedAt: Date.now() };
}

export function normalizeEstimate(raw) {
  const e = isObj(raw) ? raw : {};
  return {
    salePrice: money(e.salePrice),
    costLines: cleanLines(e.costLines, "c"),
    payoffs: cleanLines(e.payoffs, "p"),
    workLines: cleanLines(e.workLines, "w"),
    salePriceLink: e.salePriceLink === "salePrice" ? "salePrice" : "",
    salePriceManual: e.salePriceManual === true,
    workFromDeal: e.workFromDeal === true,
    skipWork: (Array.isArray(e.skipWork) ? e.skipWork : []).slice(0, MAX_LINES).map((s) => str(s, 80)).filter(Boolean),
    templateId: str(e.templateId, 40),
    templateName: str(e.templateName, 60),
    status: e.status === "shared" ? "shared" : "draft",
    shareId: str(e.shareId, 64),
    sharedAt: Number(e.sharedAt) || 0,
  };
}

export const lineTotal = (line, salePrice) =>
  line && line.kind === "percent" ? cents((money(salePrice) * percent(line.percent)) / 100) : money(line && line.amount);

const sum = (lines) => cents(lines.reduce((s, l) => s + l.total, 0));

// Every line's dollars and the bottom line: sale price less closing costs, payoffs and owed work.
export function computeEstimate(raw) {
  const e = normalizeEstimate(raw);
  const withTotals = (lines) => lines.map((l) => ({ ...l, total: lineTotal(l, e.salePrice) }));
  const costs = withTotals(e.costLines);
  const payoffs = withTotals(e.payoffs);
  const work = withTotals(e.workLines);
  const costTotal = sum(costs);
  const payoffTotal = sum(payoffs);
  const workTotal = sum(work);
  const totalDeductions = cents(costTotal + payoffTotal + workTotal);
  return { ...e, costs, payoffs, work, costTotal, payoffTotal, workTotal, totalDeductions, net: cents(e.salePrice - totalDeductions) };
}

// A fresh share id: a save while shared starts a new share, which is how an edit resets the signatures.
export function newShareId(now = Date.now(), rand = Math.random) {
  return `ns_${now.toString(36)}_${Math.floor(rand() * 1e9).toString(36)}`;
}

// What Save Draft writes. A shared estimate stays shared but gets a new share id, so every seller signs the
// changed figures again (Henry 10/06/2026: once shared, edits reset signatures and re-share).
export function draftPatch(estimate, { userId = "", now = Date.now(), rand } = {}) {
  const e = normalizeEstimate(estimate);
  const stillShared = e.status === "shared";
  return {
    ...e,
    status: stillShared ? "shared" : "draft",
    shareId: stillShared ? newShareId(now, rand) : e.shareId,
    sharedAt: stillShared ? now : e.sharedAt,
    updatedAt: now,
    updatedBy: String(userId || ""),
  };
}

// What Share to Portal for Signature writes: shared, with a new share id (signatures start over).
export function sharePatch(estimate, { userId = "", now = Date.now(), rand } = {}) {
  const e = normalizeEstimate(estimate);
  return { ...e, status: "shared", shareId: newShareId(now, rand), sharedAt: now, sharedBy: String(userId || ""), updatedAt: now, updatedBy: String(userId || "") };
}

// What the deal page shows about signing: "draft" | "shared" (waiting) | "signed" (every seller, this share).
// currentKey (figuresKey of the estimate resolved against the deal right now) is compared with the figures
// the server stored with the signatures (sigDoc.figures): once signed, a difference is changedAfterSigned
// (the signed PDF stays the record; the card offers Re-share for Signature); before every seller has
// signed, signatures given for other figures are stale and no longer count (the server agrees).
export function signingState(deal, sigDoc, currentKey = "") {
  const e = normalizeEstimate(deal && deal.netSheet);
  if (e.status !== "shared" || !e.shareId) return { state: "draft", signatures: [], required: [], pdfUrl: "", pdfError: "", stale: false, changedAfterSigned: false };
  const doc = isObj(sigDoc) && sigDoc.shareId === e.shareId ? sigDoc : null;
  const signedKey = doc && isObj(doc.figures) ? figuresKey(computeEstimate(doc.figures)) : "";
  const differs = !!(currentKey && signedKey && currentKey !== signedKey);
  const stamp = isObj(deal && deal.netSheetSigned) && deal.netSheetSigned.shareId === e.shareId ? deal.netSheetSigned : null;
  const signed = !!((doc && doc.completedShareId === e.shareId) || stamp);
  const stale = !signed && differs;
  const sigs = doc && isObj(doc.signatures) && !stale ? Object.values(doc.signatures).filter((s) => isObj(s) && Number(s.signedAt) > 0) : [];
  return {
    state: signed ? "signed" : "shared",
    stale,
    changedAfterSigned: signed && differs,
    signatures: sigs
      .map((s) => ({ name: str(s.sellerName, 120), typedName: str(s.typedName, 100), signedAt: Number(s.signedAt) }))
      .sort((a, b) => a.signedAt - b.signedAt),
    required: doc && Array.isArray(doc.required) ? doc.required : [],
    pdfUrl: str((doc && doc.pdfUrl) || (stamp && stamp.pdfUrl), 400),
    pdfError: str(doc && doc.pdfError, 200),
  };
}

// Reorder helper for the editor's up/down arrows.
export function moveLine(lines, index, delta) {
  const list = Array.isArray(lines) ? [...lines] : [];
  const to = index + delta;
  if (index < 0 || index >= list.length || to < 0 || to >= list.length) return list;
  const [it] = list.splice(index, 1);
  list.splice(to, 0, it);
  return list;
}

export const isListing = (deal) => String((deal && deal.dealtype) || "").trim().toLowerCase() !== "buyer";
